import { type Card, evaluateBest } from '@tctp/engine/browser';

const SAMPLES = 4_000;

/** Each hand's share of the pot over the given completed boards (ties split), as fractions. */
function shares(hands: Card[][], board: Card[], runouts: Card[][]): number[] {
  const won = hands.map(() => 0);
  for (const rest of runouts) {
    const full = [...board, ...rest];
    const strengths = hands.map(h => evaluateBest([...h, ...full]));
    const best = Math.max(...strengths);
    const winners = strengths.filter(s => s === best).length;
    strengths.forEach((s, i) => { if (s === best) won[i]! += 1 / winners; });
  }
  return won.map(w => w / runouts.length);
}

function unknownCards(hands: Card[][], board: Card[], dead: Card[]): Card[] {
  const taken = new Set([...hands.flat(), ...board, ...dead]);
  return Array.from({ length: 52 }, (_, c) => c).filter(c => !taken.has(c));
}

/** Every remaining board enumerated (`toCome` ≤ 2 in practice: at most C(47, 2) = 1,081 boards). */
export function exactEquity(hands: Card[][], board: Card[], dead: Card[]): number[] {
  const unknown = unknownCards(hands, board, dead);
  const toCome = 5 - board.length;
  const runouts: Card[][] = [];
  const pick = (from: number, chosen: Card[]): void => {
    if (chosen.length === toCome) { runouts.push(chosen); return; }
    for (let i = from; i < unknown.length; i++) pick(i + 1, [...chosen, unknown[i]!]);
  };
  pick(0, []);
  return shares(hands, board, runouts);
}

/** `n` random remaining boards drawn with `crypto.getRandomValues`. */
export function sampledEquity(hands: Card[][], board: Card[], dead: Card[], n = SAMPLES): number[] {
  const unknown = unknownCards(hands, board, dead);
  const toCome = 5 - board.length;
  const runouts: Card[][] = [];
  const random = new Uint32Array(toCome);
  for (let s = 0; s < n; s++) {
    crypto.getRandomValues(random);
    const deck = [...unknown];
    for (let k = 0; k < toCome; k++) {   // partial Fisher–Yates: the first `toCome` slots are the draw
      const j = k + (random[k]! % (deck.length - k));
      [deck[k], deck[j]] = [deck[j]!, deck[k]!];
    }
    runouts.push(deck.slice(0, toCome));
  }
  return shares(hands, board, runouts);
}

/**
 * The fraction of the pot each hand wins over the remaining board (ties split). Unknown cards are the 52 minus the hands,
 * the board and `dead` (every revealed discard). Exact with ≤ 2 cards to come, else 4,000 sampled boards (within ±1 % of exact).
 */
export function equity(hands: Card[][], board: Card[], dead: Card[]): number[] {
  return board.length >= 3 ? exactEquity(hands, board, dead) : sampledEquity(hands, board, dead);
}
